import Firebase, { getReference } from './Firebase';
import { storeFirebase } from '@Stores/StoreFirebase';
import { storeProduct } from '@Stores/StoreProduct';
import { isFunction, isNaN, isSomething } from '@Utils/Utils';
import { get as mget, set as mset, remove as mremove, has as mhas, keys as mkeys } from 'mobx';
import toPairs from 'lodash/toPairs';
import keys from 'lodash/keys';
import { storeManager } from '@Stores/MainStore';
import StoreAuth from '@Stores/User/StoreAuth';
import { logn } from '@Utils/PikaLog';

const updateCart = (snapshotValue) => {
    return new Promise((resolve, reject) => {
        storeFirebase.snapshotValue = snapshotValue

        if (isSomething(snapshotValue)) {
            let totalPrice = 0, totalPV = 0
            toPairs(snapshotValue).map(([id, value]) => {
                const { qty, price, pv } = value
                totalPrice += price * qty
                totalPV += pv * qty
            })

            storeFirebase.totalPrice = totalPrice
            storeFirebase.totalPV = totalPV
        } else {
            storeFirebase.totalPrice = 0
            storeFirebase.totalPV = 0
        }

        // replaced current mobx cart with firebase cart
        storeFirebase.cart = snapshotValue

        resolve()
    })
}

export const initFirebaseCart = () => {

    if (StoreAuth.isAuthorized) {
        logn(`️‍🔥 Connecting with our hottest Firebase cart...`)
        const db = Firebase.database()
        const refCart = getReference() 
        const remoteCart = db.ref(refCart)
        remoteCart.on('value', (snapshot) => {
            const snapshotValue = snapshot.val() || {}
            // console.log('FirebaseCart =>', snapshotValue)
            keys(snapshotValue).map((key) => {
                const item = snapshotValue[key]
                storeFirebase.assignToCart(item.id, item.qty)
            })

            updateCart(snapshotValue)
                .then(() => {
                    storeManager.isCartRestoredDone = true
                })
        })
    }
}

export const useFirebaseCart = () => {
    const db = Firebase.database()
    const refCart = getReference()
    const remoteCart = db.ref(refCart)

    const validateQty = qty => isNaN(parseInt(qty)) === false
    const forceUpdate = () => remoteCart.set(storeFirebase.snapshotValue)

    /**
     * @param {'set'|'add'|'remove'} type 
     * @param {string} itemCode 
     * @param {array} listItems 
     * @param {number|string} qty 
     * @param {*} product 
     * @param {*} price 
     */
    const update = (type, itemCode, qty) => {
        if (storeProduct.hasProduct(itemCode)) {
            const legitQty = validateQty(qty) ? parseInt(qty) : 0
            const product = storeProduct.getProduct(itemCode)
            const { price, pv = 0 } = product

            // console.log('[1] update firebase | current snapshot >>', toJS(storeFirebase.snapshotValue))
            const foundItem = mhas(storeFirebase.snapshotValue, itemCode)
            // console.log('foundItem?', foundItem)

            if (foundItem) {
                // console.log('qty', validateQty(qty), parseInt(qty), legitQty)
                // update existed item qty
                const cartItem = mget(storeFirebase.snapshotValue, itemCode)
                const newQty = (type === 'set' ? legitQty : parseInt(cartItem.qty) + legitQty)
                if (newQty > 0) {
                    mset(storeFirebase.snapshotValue, {
                        [itemCode]: {
                            qty: newQty,
                            price: price,
                            pv: pv
                        }
                    })
                } else {
                    mremove(storeFirebase.snapshotValue, itemCode)
                }
            } else {
                if (legitQty > 0) {
                    // set new item and its qty
                    mset(storeFirebase.snapshotValue, {
                        [itemCode]: {
                            qty: legitQty,
                            price: price,
                            pv: pv
                        }
                    })
                } else {
                    mremove(storeFirebase.snapshotValue, itemCode)
                }
            }
        } else {
            mremove(storeFirebase.snapshotValue, itemCode)
        }

        // console.log('[2] update firebase | new snapshot >>', toJS(storeFirebase.snapshotValue))

        forceUpdate()
    }

    const clear = () => remoteCart.remove()
    const set = (value) => remoteCart.set(value)

    const addToCart = (itemCode, qty = 1) => update('add', itemCode, qty)
    const removeFromCart = (itemCode, qty = 1) => update('remove', itemCode, -qty)
    const assignToCart = (itemcode, qty) => update('set', itemcode, qty)

    const listenToItem = (itemCode, callback) => {
        const remoteItem = Firebase.database().ref(getReference({ itemCode: itemCode }))
        if (StoreAuth.isAuthorized) {
            remoteItem.on('value', snap => {
                if (isFunction(callback)) {
                    callback(snap.val() || {})
                }
            })
        }
    }

    const unlistenToItem = (itemCode) => {
        if (StoreAuth.isAuthorized) {
            Firebase.database().ref(getReference({ itemCode: itemCode })).off()
        }
    }

    const getCurrentCartItems = async () => {
        let items = []

        if (StoreAuth.isAuthorized) {
             const snapshot = await remoteCart.once('value')
             const objSnap = await snapshot.val()
             items = Object.keys(objSnap).map(itemCode => ({ itemCode, ...objSnap[itemCode]} ))
        }

        return items
    }

    const removeItem = itemCode => {
        if (StoreAuth.isAuthorized) {
            return Firebase.database().ref(getReference({ itemCode: itemCode })).remove()
        }
    }

    return {
        cart: remoteCart,
        clear,
        set,
        removeFromCart,
        addToCart,
        assignToCart,
        listenToItem,
        unlistenToItem,
        getCurrentCartItems,
        removeItem,
    }
}